/**
 * 분석 지도(AnalysisMap)의 칸 판정 — 순수 함수.
 *
 * 칸 하나가 답하는 것은 「AI가 끝냈나」가 아니라 **「이 문제, 지금 누구 차례인가」**다.
 * ⑤의 완료 조건이 둘이기 때문이다. 백엔드 계약이 두 집계의 정의로 그것을 못 박는다
 * (docs/ocr-hub-v2/ocr-pipeline-stage-model-backend-handoff.html §4.5):
 *   review_pending_count   = review_status 100 이고 analysis_status ≠ 200 — 원본 미검수(④의 조건)
 *   rereview_pending_count = review_status 100 이고 analysis_status = 200 — AI가 되돌려 보낸 것(⑤의 조건)
 *
 * 종전에는 analysis_status 하나만 그려 **분석이 끝나 되돌아온 문제와 승인까지 끝난 문제가 같은 초록**
 * 이었다. 같은 단계의 배지는 「재검수 대기 N건」이라 말하는데 지도가 그 N건을 못 짚었다 —
 * 이 지도가 만들어진 이유(「남은 6건이 어느 문제인지 알 수 없었다」)와 같은 결함이다.
 *
 * 화면 없이 검사할 수 있는 규칙이라 형제 모듈로 뗀다(reviewRowPatch.js·numberPlan.js와 같은 관례).
 */

/**
 * 분석이 끝난 문제가 아직 사람의 확인을 기다리는가.
 *
 * 분석이 끝나면 그 문제는 검수대기(100)로 되돌아온다 — OCR_REVIEW_STATUS 주석의
 * 「100 검수대기(AI 생성 시 재검수로 복귀 포함)」가 그것이다. 그래서 analysis 200 만으로는
 * 승인 여부를 알 수 없다.
 *
 * 판정을 「review 100 인가」가 아니라 **「review 200 이 아닌가」**로 두는 이유:
 * 반려(400)는 계약상 여기 올 수 없지만(반려하면 백엔드가 analysis_status를 100으로 되돌린다)
 * 만에 하나 오더라도 **승인이 아닌 것을 승인 색으로 그리지 않기** 위해서다.
 * 값이 비어 온 경우도 같은 이유로 승인 쪽에 넣지 않는다 — 모르면 초록이 아니다.
 */
export const needsRereview = (p) => p?.analysis_status === 200 && p?.review_status !== 200;

/**
 * 칸 색. OCR_ANALYSIS_STATUS의 톤을 따르되 칸은 테두리가 있어 border를 더한다.
 *
 * 재검수 대기의 호박은 새로 고른 색이 아니다 — 그 상태가 곧 review_status 100(검수대기)이고
 * OCR_REVIEW_STATUS가 그 값에 이미 호박을 쓴다. 같은 단계의 「재검수 대기 N건」 배지와도 같은
 * 색이라, 배지의 숫자와 호박 칸의 개수가 서로를 검산한다.
 *
 * 실패(500)를 맨 앞에서 가르는 이유: 분석이 실패한 문제는 검수 상태가 무엇이든 실패다.
 */
export function cellStyle(status, rereview) {
  if (status === 500) return 'border-red-400 bg-red-100 font-bold text-red-700';
  if (status === 200) {
    return rereview
      ? 'border-amber-300 bg-amber-100 text-amber-800'
      : 'border-green-300 bg-green-100 text-green-800';
  }
  if (status === 150) return 'border-blue-300 bg-blue-100 text-blue-800';
  if (status === 110) return 'border-gray-300 bg-gray-100 text-gray-700';
  return 'border-gray-200 bg-white text-gray-600'; // 100 미실행 · 값 없음
}
